import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { destinoAposLoginDoEstado } from '@/lib/authReturnTarget';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { ExternalLink } from 'lucide-react';
import { PasswordInput } from '@/components/ui/password-input';
import logoOnDiet from '@/assets/logo_ondiet.webp';
import SimpleLoading from '@/components/SimpleLoading';
import { AppBootError } from '@/components/AppBootError';
import { completeAppOpen } from '@/lib/appOpenTelemetry';

const loginSchema = z.object({
  email: z.string().trim().email({ message: 'Email inválido' }),
  password: z.string().min(8, { message: 'Senha deve ter no mínimo 8 caracteres' }),
});

const REMEMBERED_EMAIL_KEY = 'ondiet_remembered_email';

const Login = () => {
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? '');
  const [password, setPassword] = useState('');
  const [rememberEmail, setRememberEmail] = useState(() => !!localStorage.getItem(REMEMBERED_EMAIL_KEY));
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const { signIn, user, loading, bootstrapError, retryBootstrap } = useAuth();
  const location = useLocation();
  const postLoginReturnTo = destinoAposLoginDoEstado(location.state);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Força theme-color preto na tela de login
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]:not([media])') 
      || document.querySelector('meta[name="theme-color"]');
    const originalContent = metaThemeColor?.getAttribute('content');
    
    // Remove as meta tags com media query e adiciona uma fixa preta
    const existingMetas = document.querySelectorAll('meta[name="theme-color"]');
    existingMetas.forEach(meta => meta.remove());
    
    const newMeta = document.createElement('meta');
    newMeta.name = 'theme-color';
    newMeta.content = '#000000';
    document.head.appendChild(newMeta);
    
    return () => {
      // Restaura as meta tags originais ao sair
      newMeta.remove();
      const lightMeta = document.createElement('meta');
      lightMeta.name = 'theme-color';
      lightMeta.content = '#FEFEFE';
      lightMeta.media = '(prefers-color-scheme: light)';
      document.head.appendChild(lightMeta);
      
      const darkMeta = document.createElement('meta');
      darkMeta.name = 'theme-color';
      darkMeta.content = '#000000';
      darkMeta.media = '(prefers-color-scheme: dark)';
      document.head.appendChild(darkMeta);
    };
  }, []);

  // Redireciona se já logado
  useEffect(() => {
    if (bootstrapError) {
      completeAppOpen('auth.unavailable', 'unavailable');
    } else if (!loading && user) {
      navigate(postLoginReturnTo, { replace: true });
    } else if (!loading && !user && !bootstrapError) {
      completeAppOpen('login', 'login');
    }
  }, [bootstrapError, loading, user, navigate, postLoginReturnTo]);

  if (bootstrapError) {
    return <AppBootError onRetry={retryBootstrap} />;
  }

  // Mostra loading simples enquanto verifica sessão
  if (loading) {
    return <SimpleLoading />;
  }

  // Não renderiza o form se já estiver logado
  if (user) {
    return null;
  }

  const hasCaptcha = !!import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const captchaReady = !hasCaptcha || !!captchaToken;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const validatedData = loginSchema.parse({ email, password });
      const { error } = await signIn(validatedData.email, validatedData.password, captchaToken ?? undefined);

      if (error) {
        toast({
          title: 'Erro ao entrar',
          description: error.message === 'Invalid login credentials'
            ? 'Email ou senha incorretos'
            : error.message,
          variant: 'destructive',
        });
      } else {
        if (rememberEmail) {
          localStorage.setItem(REMEMBERED_EMAIL_KEY, validatedData.email);
        } else {
          localStorage.removeItem(REMEMBERED_EMAIL_KEY);
        }
        navigate(postLoginReturnTo, { replace: true });
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: 'Erro de validação',
          description: error.errors[0].message,
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
      turnstileRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-black flex items-center justify-center px-6 py-safe animate-fade-in">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img 
            src={logoOnDiet} 
            alt="OnDiet" 
            className="h-20 mx-auto mb-4"
          />
          <p className="text-[#8A8A8A] text-sm">Entre na sua conta</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#1a1a1a] border-[#333] text-white placeholder:text-[#666] h-12"
              disabled={isLoading}
              required
            />
            <PasswordInput
              placeholder="Senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#1a1a1a] border-[#333] text-white placeholder:text-[#666] h-12"
              disabled={isLoading}
              required
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberEmail}
              onChange={(e) => setRememberEmail(e.target.checked)}
              className="w-4 h-4 rounded border-[#333] bg-[#1a1a1a] accent-white"
            />
            <span className="text-[#8A8A8A] text-sm">Lembrar meu email</span>
          </label>

          {hasCaptcha && (
            <div className="hidden">
              <Turnstile
                ref={turnstileRef}
                siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                onSuccess={setCaptchaToken}
                onError={() => {
                  setCaptchaToken(null);
                  turnstileRef.current?.reset();
                  toast({
                    title: 'Erro de verificação',
                    description: 'Tentando novamente...',
                    variant: 'destructive',
                  });
                }}
                onExpire={() => setCaptchaToken(null)}
                options={{ theme: 'dark', size: 'invisible' }}
              />
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-white text-black hover:bg-gray-100 h-12 text-sm tracking-wider"
            disabled={isLoading || !captchaReady}
          >
            {isLoading ? 'Entrando...' : !captchaReady ? 'Verificando segurança...' : 'Entrar'}
          </Button>

          <div className="text-center">
            <Link
              to="/forgot-password"
              className="text-[#8A8A8A] text-sm hover:text-white transition-colors"
            >
              Esqueceu a senha?
            </Link>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                const url = 'https://ondiet.app/start';
                if (typeof window !== 'undefined' && window.median?.window?.open) {
                  window.median.window.open(url, 'external');
                } else {
                  window.open(url, '_blank', 'noopener,noreferrer');
                }
              }}
              className="text-[#8A8A8A] text-sm hover:text-white transition-colors inline-flex items-center gap-1.5"
            >
              Não tem conta?
              <ExternalLink size={13} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
